import React, { useState, useEffect } from 'react';
import axios from 'axios';

const HistoricMaps = () => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableSystems, setAvailableSystems] = useState([]);
  const [selectedSystem, setSelectedSystem] = useState('');
  const [availablePlots, setAvailablePlots] = useState([]);
  const [selectedPlot, setSelectedPlot] = useState('');
  const [isLoadingSystems, setIsLoadingSystems] = useState(false);
  const [isLoadingPlots, setIsLoadingPlots] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  // Fetch user ID on component mount
  useEffect(() => {
    const getUserId = async () => {
      try {
        const token = localStorage.getItem('access');
        if (!token) throw new Error('No token found. Please log in again.');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/auth/users/me/`, {
          headers: { Authorization: `JWT ${token}`, 'Content-Type': 'application/json' },
        });
        setUserId(response.data.id);
      } catch (error) {
        console.error('Error fetching user ID:', error);
      }
    };
    getUserId();
  }, []);

  // Fetch systems when the month is selected
  const handleMonthChange = async (event) => {
    const month = event.target.value;
    setSelectedMonth(month);
    setSelectedSystem('');
    setAvailableSystems([]);
    setAvailablePlots([]);
    setSelectedPlot('');

    if (month && userId) {
      setIsLoadingSystems(true);
      try {
        const response = await axios.get(
          `https://yjdezf2t2e.execute-api.us-west-2.amazonaws.com/dev3/systems`,
          {
            params: { userId, month },
          }
        );
        if (response.data && Array.isArray(response.data.systems)) {
          if (response.data.systems.length === 0) {
            setIsErrorModalOpen(true);
            setModalMessage(`No systems found for the selected month: ${month}.`);
          } else {
            setAvailableSystems(response.data.systems);
          }
        } else {
          console.error('Error: systems data not found or invalid in response.');
          setAvailableSystems([]);
        }
      } catch (error) {
        console.error('Error fetching systems:', error);
        setAvailableSystems([]);
        setIsErrorModalOpen(true);
        setModalMessage('No systems were active for this month. Please select a different month.');
      } finally {
        setIsLoadingSystems(false);
      }
    }
  };

  // Fetch plots when the system is selected
  const handleSystemChange = async (event) => {
    const system = event.target.value;
    setSelectedSystem(system);
    setAvailablePlots([]);
    setSelectedPlot('');

    if (system && selectedMonth && userId) {
      setIsLoadingPlots(true);
      try {
        const response = await axios.get(
          `https://yjdezf2t2e.execute-api.us-west-2.amazonaws.com/dev3/generateMap`,
          {
            params: { userId, month: selectedMonth, system },
          }
        );
        if (response.data && Array.isArray(response.data.plot_urls)) {
          const plots = response.data.plot_urls.map((url) => {
            const name = url.substring(url.lastIndexOf('/') + 1, url.lastIndexOf('.'));
            return { name, url }; // Name and URL for each plot
          });
          setAvailablePlots(plots);
        } else {
          console.error('Error: plot_urls not found or invalid in response.');
          setAvailablePlots([]);
        }
      } catch (error) {
        console.error('Error fetching plots:', error);
        setAvailablePlots([]);
      } finally {
        setIsLoadingPlots(false);
      }
    }
  };

  const handlePlotChange = (event) => {
    const selectedUrl = event.target.value;
    setSelectedPlot(selectedUrl ? `${selectedUrl}?t=${new Date().getTime()}` : '');
  };
  
  const openModal = () => {
    const cleanSelectedPlot = selectedPlot.split('?')[0]; // Remove timestamp
    const selectedPlotObject = availablePlots.find((plot) => plot.url === cleanSelectedPlot);
  
    if (selectedPlotObject) {
      setModalMessage(
        `<img src="${selectedPlotObject.url}?t=${new Date().getTime()}" alt="${selectedPlotObject.name}" 
          style="width: 100%; height: auto; max-height: 95vh;" />`
      );
      setIsModalOpen(true);
    } else {
      console.error('Selected plot not found in availablePlots.');
    }
  };  

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
  };

  const isButtonDisabled = !selectedMonth || !selectedSystem || !selectedPlot;

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h1 style={{ fontSize: '36px', fontWeight: '500', marginBottom: '30px' }}>Historic Moisture Maps</h1>
      <h6 style={{ marginBottom: '20px' }}>Select a Month, a System, and an available Date Range to Generate your Map</h6>

      {/* Month Dropdown */}
      <div style={{ marginBottom: '30px', width: '400px', margin: 'auto' }}>
        <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', textAlign: 'left' }}>Select Month:</label>
        <select
          value={selectedMonth}
          onChange={handleMonthChange}
          style={{ width: '400px', padding: '8px', fontSize: '16px', borderRadius: '5px' }}
        >
          <option value="">Select Month</option>
          {months.map((month) => (
            <option key={month} value={month}>
              {month}
            </option>
          ))}
        </select>
      </div>

      {/* Systems Dropdown */}
      {selectedMonth && (
        <div style={{ marginBottom: '30px', width: '400px', margin: 'auto', marginTop: '20px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', textAlign: 'left' }}>Select System:</label>
          <select
            value={selectedSystem}
            onChange={handleSystemChange}
            style={{ width: '400px', padding: '8px', fontSize: '16px', borderRadius: '5px' }}
            disabled={isLoadingSystems || availableSystems.length === 0}
          >
            <option value="">Select System</option>
            {isLoadingSystems ? (
              <option>Loading systems...</option>
            ) : (
              availableSystems.map((system) => (
                <option key={system} value={system}>
                  {system}
                </option>
              ))
            )}
          </select>
        </div>
      )}

      {/* Plots Dropdown */}
      {selectedSystem && (
        <div style={{ marginBottom: '30px', width: '400px', margin: 'auto', marginTop: '20px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', textAlign: 'left' }}>
            Select Start Date:
          </label>
          <select
            value={selectedPlot.split('?')[0]} // Ignore timestamp when setting selected value
            onChange={handlePlotChange}
            style={{ width: '400px', padding: '8px', fontSize: '16px', borderRadius: '5px' }}
            disabled={isLoadingPlots || availablePlots.length === 0}
          >
            <option value="">Select Start Date</option>
            {isLoadingPlots ? (
              <option>Loading graphs...</option>
            ) : (
              availablePlots.map((plot, index) => (
                <option key={index} value={plot.url}>
                  {plot.name}
                </option>
              ))
            )}
          </select>
        </div>
      )}

      {/* Generate Button */}
      <button
        onClick={openModal}
        disabled={isButtonDisabled}
        style={{
          backgroundColor: isButtonDisabled ? '#A9A9A9' : '#4CAF50',
          color: 'white',
          marginTop: '30px',
          padding: '14px 28px',
          fontSize: '18px',
          borderRadius: '5px',
          cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.3s ease', // Smooth transition for hover
        }}
        onMouseEnter={(e) => {
          if (!isButtonDisabled) {
            e.target.style.backgroundColor = '#45a049'; // Darker green on hover
          }
        }}
        onMouseLeave={(e) => {
          if (!isButtonDisabled) {
            e.target.style.backgroundColor = '#4CAF50'; // Reset to original color
          }
        }}
      >
        Generate
      </button>

      {/* Modal for 1 x 4*/}
      {isModalOpen && (
        <div
          className="modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px', // Increased padding for better spacing
              borderRadius: '8px',
              width: '90%', // Adjust width to be more mobile-friendly
              maxWidth: '390px', // Still use a max-width for larger screens
              maxHeight: '90vh', // Limit height to avoid overflowing on smaller screens
              overflow: 'auto',
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '5px',
                right: '5px',
                backgroundColor: 'red',
                color: 'white',
                border: 'none',
                padding: '8px 12px',
                cursor: 'pointer',
              }}
            >
              X
            </button>
            <p
              style={{
                textAlign: 'center',
                color: 'black',
                marginTop: '20px',
                fontWeight: 'bold',
              }}
            >
              Right-click to "Save image as..." or hold to save image on mobile device
            </p>
            <div dangerouslySetInnerHTML={{ __html: modalMessage }} />
          </div>
        </div>
      )}

      {/* Error Modal */}
      {isErrorModalOpen && (
        <div
          className="modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              textAlign: 'center',
              width: '50%',
              maxWidth: '400px',
              position: 'relative',
            }}
          >
            <button
              onClick={closeErrorModal}
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                backgroundColor: 'red',
                color: 'white',
                border: 'none',
                padding: '5px 10px',
                cursor: 'pointer',
              }}
            >
              X
            </button>
            <h2 style={{ color: 'red' }}>Alert</h2>
            <p>{modalMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricMaps;
